import {createDivIn, createImgIn, createLinkIn, setElementInnerHTML} from './ui_utils/DomUtils'
import {EnumUtils} from '@colonist/utils'
import {UIImagePathController} from './UIEnums.ts'
import {getNestedTranslation, TranslationObject} from '@colonist/i18n'
import {ViewPopupStoreLink} from './ui_popup/ViewPopupStoreLink'
import {PlayerColor} from '@colonist/game-states'
import {navigateToPage} from './ui_utils/ClientUtils.ts'

interface PlayerColorState {
    activeColor: PlayerColor | undefined
    availableColors: PlayerColor[]
}

export abstract class UIPlayerColorController {
    private static readonly colorStoreLink = '/store#colors'
    private userState: PlayerColorState

    protected colorSelectionModal: HTMLDivElement
    protected colorsContainer: HTMLDivElement
    protected colorsGrid: HTMLDivElement

    protected abstract onColorSelect(color: PlayerColor)
    protected abstract updatePreferredColor(color: PlayerColor)

    setState(userState: PlayerColorState) {
        this.userState = userState
        this.createColors()
    }

    private createColors() {
        setElementInnerHTML(this.colorsGrid, {key: 'strings:colonistCommon.utils.emptyString'})
        EnumUtils.loopEnums(PlayerColor, (_: string, playerColor: PlayerColor) => {
            if(playerColor === PlayerColor.None) return
            this.createColorImage(playerColor)
        })
        this.showStoreLinkElement()
    }

    private createColorImage(color: PlayerColor) {
        const settlementLobbyImage = UIImagePathController.getPlayerColorBuildings(color).settlement
        const image = createImgIn(this.colorsGrid, settlementLobbyImage)
        image.onclick = this.imageOnclick(color)
        this.setColorImageStyling(color, image)
    }

    private imageOnclick(color: PlayerColor): (() => void) {
        if(this.canSelectColor(color)) return () => this.onColorSelect(color)
        return () => UIPlayerColorController.displayColorNotOwnedPopup(color)
    }

    private canSelectColor(color: PlayerColor): boolean {
        return this.userState.availableColors.includes(color)
    }

    private setColorImageStyling(color: PlayerColor, image: HTMLImageElement) {
        if(this.isColorUnavailable(color)) {
            image.classList.add('header_profile_color_transparent')
            return
        }
        if(this.isColorActive(color)) image.classList.add('active')
    }

    private isColorUnavailable(color: PlayerColor): boolean {
        if(this.canSelectColor(color)) return false
        if(color == PlayerColor.Black) return false
        return true
    }

    protected isColorActive(color: PlayerColor): boolean {
        return color == this.userState.activeColor
    }

    private static displayColorNotOwnedPopup(color: PlayerColor) {
        const defaultMessage = getNestedTranslation({key: 'strings:homePage.header.storePurchaseColorPrompt'}, {
            color: {key: `strings:colonistCommon.playerColors.${EnumUtils.getEnumKey(color, PlayerColor)}`},
        })
        const blackMessage: TranslationObject = {key: 'strings:homePage.header.defaultColorSelector.getBlackColorForFree'}
        new ViewPopupStoreLink({key: 'strings:homePage.header.defaultColorSelector.youDoNotOwnThisColor'}, color === PlayerColor.Black ? blackMessage : defaultMessage)
    }

    showColorWindow() {
        this.colorSelectionModal.style.display = 'block'
    }

    protected hideColorWindow() {
        this.colorSelectionModal.style.display = 'none'
    }

    private showStoreLinkElement() {
        const storeLinkElementClass = 'header_profile_selector_noicons'
        const storeLinkElements = this.colorsContainer.getElementsByClassName(storeLinkElementClass)
        for(const storeLinkElement of storeLinkElements) {
            storeLinkElement.remove()
        }

        const linkContainer = createDivIn(this.colorsContainer, storeLinkElementClass)
        const link = createLinkIn(linkContainer, '', {key: 'strings:homePage.header.defaultColorSelector.getMoreColors'}, '')
        link.onclick = (e: MouseEvent) => {
            e.preventDefault()
            e.stopPropagation()
            navigateToPage(UIPlayerColorController.colorStoreLink, true)
        }
    }
}
